import * as R from "ramda";
import React from "react";
import { FaTrash } from "react-icons/fa";
import styled, { css } from "styled-components";

import { ResolvableType } from "../../../api/ResolveApi/resolve-api.types";
import ActionConfirmationButton from "../../../components/pages/CourseAuthor/ActionConfirmation";
import { usePagination } from "../../../components/utils/Pagination/hooks";
import {
  ActionRow,
  BottomPagination,
  Count,
  TopPagination,
} from "../../../components/utils/Pagination/NumberedPagination/common";
import { Table } from "../../../components/utils/Pagination/NumberedPagination/table";
import { PageContextProvider } from "../../../context/numbered-pagination";
import { dependencies } from "../../../dependencies";
import { pluralize } from "../../../utils";

const { api } = dependencies;

const Placeholder = styled.div`
  width: 11rem;
`;

const narrowColumn = css`
  text-align: center;
  width: 10rem;
`;

const CustomActionRow = styled(ActionRow)`
  flex-direction: row;
`;

interface IUserRow {
  name: string;
  actions: React.ReactNode;
}

interface IMembersProps {
  accountId: string;
  deleteMember: (memberId: string) => void;
  memberIds: string[];
}

interface IMemberInfo {
  name: string;
  deleted: boolean;
}

const toMembersMap = (memberIds: string[], members: IMemberInfo[]): Map<string, IMemberInfo> =>
  new Map(R.zip(memberIds, members) as Array<[string, IMemberInfo]>);

const Members: React.FC<IMembersProps> = props => {
  const [loading, setLoading] = React.useState(true);
  const [membersMap, setMembersMap] = React.useState(new Map<string, IMemberInfo>());
  const notDeletedMembers = Array.from(membersMap).filter(([_, { deleted }]) => !deleted);
  const { value, pagedData } = usePagination(notDeletedMembers);
  const total = notDeletedMembers.length;
  const deleteMember = (memberId: string) => () => {
    props.deleteMember(memberId);
    membersMap.delete(memberId);
    setMembersMap(new Map(membersMap));
  };

  const toUserRow = ([memberId, memberInfo]: [string, IMemberInfo]): IUserRow => ({
    actions: <ActionConfirmationButton action="Delete" actionFn={deleteMember(memberId)} ActionIcon={FaTrash} />,
    name: memberInfo.name,
  });

  React.useEffect(() => {
    api.resolve.displayInfo
      .post(
        props.accountId,
        props.memberIds.map(id => [ResolvableType.Account, id])
      )
      .then(([error, membersResp]) => {
        if (error || membersResp === null) {
          return Promise.reject("Error");
        }
        const items = membersResp.data
          .filter(item => item !== null)
          .map(item => ({
            deleted: item!.value.deleted,
            name: item!.value.displayName || "Not given name",
          }));
        setMembersMap(toMembersMap(props.memberIds, items));
        setLoading(false);
        return Promise.resolve("ok");
      })
      .catch(() => setLoading(true));
  }, [props.memberIds]);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <div>
      <PageContextProvider value={value}>
        <CustomActionRow>
          <Placeholder />
          <TopPagination total={total} />
          <Count>{pluralize("Member", total)}</Count>
        </CustomActionRow>
        <Table
          columns={[
            { name: "Name", accessor: "name" },
            { name: "Actions", accessor: "actions", styles: narrowColumn },
          ]}
          data={pagedData.map(toUserRow)}
        />
        <BottomPagination total={total} />
      </PageContextProvider>
    </div>
  );
};

export default Members;
